import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "./cn";

const CONTROL = cn(
  "w-full rounded-md border border-line bg-surface px-3 text-sm text-ink",
  "placeholder:text-ink-faint",
  "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent focus-visible:border-accent",
  "disabled:opacity-60 disabled:cursor-not-allowed",
  // Touch targets stay at 44px on small screens.
  "h-9 max-md:h-11",
);

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(CONTROL, invalid && "border-risk focus-visible:outline-risk", className)}
      {...rest}
    />
  );
});

export interface FieldProps {
  label: string;
  htmlFor?: string;
  /** Validation message. Its presence is what marks the field as failed. */
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Label + control + message, the layout every form in the suite uses.
 *
 * The `data-field` marker is what lets {@link Dialog} stretch form controls to
 * the width of the modal without touching anything else inside it — a compact
 * input in a table row, or a toolbar select, is not wrapped in a Field and so
 * keeps whatever width its call site asked for.
 */
export function Field({ label, htmlFor, error, hint, required, children, className }: FieldProps) {
  return (
    <div data-field="" className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-ink-muted">
        {label}
        {required && <span className="text-risk ml-0.5">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-[12px] text-risk">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}
